import { forwardRef, useMemo, type SelectHTMLAttributes } from 'react';
import { useParts } from '@/features/parts/queries';
import { cn } from '@/lib/utils';

interface PartPickerProps
  extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  value: string | null;
  onChange: (partId: string | null) => void;
  /** Hide inactive parts from the list (default: true). */
  hideInactive?: boolean;
  /** Allow the empty option (default: true). */
  allowEmpty?: boolean;
  emptyLabel?: string;
  /** Optional: filter to a specific partTypeId. */
  partTypeId?: string;
  /** Optional: exclude a specific partId from the list (used to
   *  prevent declaring "A is equivalent to A" in the equivalences
   *  modal). */
  excludePartId?: string;
}

/**
 * PartPicker — native <select> wired to useParts() with optional
 * filters. Used by the equivalences modal to pick a target Part to
 * declare equivalent to the current one.
 *
 * Each option shows "{name} ({code})" with the manufacturer and
 * partNumber appended in parentheses when present so users can
 * disambiguate Parker 925034 from Hydac 925034 in real catalogs.
 */
export const PartPicker = forwardRef<HTMLSelectElement, PartPickerProps>(
  (
    {
      value,
      onChange,
      hideInactive = true,
      allowEmpty = true,
      emptyLabel,
      partTypeId,
      excludePartId,
      className,
      ...rest
    },
    ref,
  ) => {
    const { data, isLoading } = useParts(
      partTypeId ? { partTypeId } : undefined,
    );

    const visible = useMemo(() => {
      if (!data) return [];
      return data.filter((p) => {
        if (hideInactive && !p.isActive) return false;
        if (excludePartId && p.id === excludePartId) return false;
        return true;
      });
    }, [data, hideInactive, excludePartId]);

    return (
      <select
        ref={ref}
        value={value ?? ''}
        onChange={(e) =>
          onChange(e.target.value === '' ? null : e.target.value)
        }
        disabled={isLoading || rest.disabled}
        className={cn(
          'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50',
          className,
        )}
        {...rest}
      >
        {allowEmpty && <option value="">{emptyLabel ?? '—'}</option>}
        {visible.map((p) => {
          const suffix =
            p.manufacturer || p.manufacturerPartNumber
              ? ` — ${[p.manufacturer, p.manufacturerPartNumber]
                  .filter(Boolean)
                  .join(' ')}`
              : '';
          return (
            <option key={p.id} value={p.id}>
              {p.name} ({p.code}){suffix}
            </option>
          );
        })}
      </select>
    );
  },
);
PartPicker.displayName = 'PartPicker';
